import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { listMaterials, getMaterialVariantDimensions } from "../api/materials";
import { createSalesOrderItem, updateSalesOrderItem, getSalesOrderItemAudit } from "../api/salesOrders";
import MaterialVariantSelectModal from "./MaterialVariantSelectModal";

// Değişiklik geçmişinde alan adlarının Türkçe karşılıkları.
const FIELD_LABELS = {
  material_id: "Malzeme",
  material_variant_id: "Varyant",
  customer_product_code: "Müşteri Ürün Kodu",
  inner_carton_code: "İç Koli Kodu",
  outer_carton_code: "Dış Koli Kodu",
  product_name_text: "Ürün Adı",
  qty: "Miktar",
  due_date: "Termin",
  po_date: "PO Tarihi",
  pi_date: "PI Tarihi",
  note: "Not",
  status: "Durum",
  manufacturing_source: "Üretim Kaynağı",
};

const MFG_SOURCE_LABELS = { 1: "Bobin", 2: "Fason" };

function formatAuditValue(field, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (field === "manufacturing_source") return MFG_SOURCE_LABELS[Number(value)] || String(value);
  if (field === "qty") return Number(value).toLocaleString("tr-TR");
  if (["due_date", "po_date", "pi_date"].includes(field)) {
    const [y, m, d] = String(value).slice(0, 10).split("-");
    return y && m && d ? `${d}.${m}.${y}` : String(value);
  }
  return String(value);
}

function formatAuditDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Tarih alanlarını <input type="date"> formatına indirger. */
function toDateInput(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

export default function SalesOrderItemModal({ salesOrderId, item = null, onClose, onSaved }) {
  const isEdit = !!item;
  const [materials, setMaterials] = useState([]);
  const [materialId, setMaterialId] = useState("");
  const [variantTemplate, setVariantTemplate] = useState([]);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [resolvedVariantId, setResolvedVariantId] = useState(null);
  const [resolvedVariantDisplay, setResolvedVariantDisplay] = useState("");
  // Varyant seçicinin dolu açılabilmesi için o an geçerli olan seçimler.
  const [variantSelections, setVariantSelections] = useState(null);
  const [qty, setQty] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [customerProductCode, setCustomerProductCode] = useState("");
  const [innerCartonCode, setInnerCartonCode] = useState("");
  const [outerCartonCode, setOuterCartonCode] = useState("");
  const [productNameText, setProductNameText] = useState("");
  const [poDate, setPoDate] = useState("");
  const [piDate, setPiDate] = useState("");
  const [manufacturingSource, setManufacturingSource] = useState(1); // 1 = Bobin, 2 = Fason
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Varyantın sıfırlanması ve seçicinin açılması SADECE kullanıcı malzemeyi
  // kendi değiştirdiğinde olmalı; forma mevcut kalem doldurulurken olmamalı.
  const userPickedMaterialRef = useRef(false);
  // Kaydederken "sadece değişeni gönder" karşılaştırması için açılıştaki değerler.
  const initialRef = useRef(null);

  useEffect(() => {
    // Only show "ürünler" in sales order items (mamül)
    listMaterials({ mattype_code: 3 })
      .then(setMaterials)
      .catch((err) => {
        console.error("MATERIALS LOAD ERROR", err);
        setError(
          err?.response?.data?.error?.message ||
            "Malzemeler yüklenemedi. Veritabanı migration’ı çalışmadıysa önce onu uygulayın, sonra backend’i yeniden başlatın."
        );
      });
  }, []);

  // Düzenleme modunda mevcut kalemi forma doldur.
  useEffect(() => {
    if (!item) return;
    const snapshot = {
      material_id: item.material_id ? String(item.material_id) : "",
      material_variant_id: item.material_variant_id ? Number(item.material_variant_id) : null,
      customer_product_code: String(item.customer_product_code || ""),
      inner_carton_code: String(item.inner_carton_code || ""),
      outer_carton_code: String(item.outer_carton_code || ""),
      product_name_text: String(item.product_name_text || ""),
      qty: item.qty === null || item.qty === undefined ? "" : String(Number(item.qty)),
      due_date: toDateInput(item.due_date),
      po_date: toDateInput(item.po_date),
      pi_date: toDateInput(item.pi_date),
      note: String(item.note || ""),
      manufacturing_source: Number(item.manufacturing_source || 1),
    };
    initialRef.current = snapshot;
    setMaterialId(snapshot.material_id);
    setResolvedVariantId(snapshot.material_variant_id);
    setResolvedVariantDisplay(item.material_variant_display_name || "");
    // Kalemin mevcut varyant değerleri seçiciye taşınır; kullanıcı yalnızca
    // değiştirmek istediği özelliği (ör. çap) değiştirsin diye.
    setVariantSelections(
      (item.variant_details || [])
        .filter((d) => Number.isFinite(Number(d?.dimension_id)))
        .map((d) => ({
          dimension_id: Number(d.dimension_id),
          option_id: d.option_id ?? null,
          value_number: d.value_number ?? null,
          value_text: d.value_text ?? null,
        }))
    );
    setCustomerProductCode(snapshot.customer_product_code);
    setInnerCartonCode(snapshot.inner_carton_code);
    setOuterCartonCode(snapshot.outer_carton_code);
    setProductNameText(snapshot.product_name_text);
    setQty(snapshot.qty);
    setDueDate(snapshot.due_date);
    setPoDate(snapshot.po_date);
    setPiDate(snapshot.pi_date);
    setNote(snapshot.note);
    setManufacturingSource(snapshot.manufacturing_source);
  }, [item]);

  useEffect(() => {
    const mid = materialId ? Number(materialId) : null;
    // Malzemeyi kullanıcı mı değiştirdi, yoksa form mu dolduruldu?
    const byUser = userPickedMaterialRef.current;
    userPickedMaterialRef.current = false;
    if (byUser) {
      // Yeni malzemenin varyant şablonu farklı olduğu için eski varyant ve
      // seçimleri geçersizdir.
      setResolvedVariantId(null);
      setResolvedVariantDisplay("");
      setVariantSelections(null);
    }
    setVariantTemplate([]);
    if (!mid) return;

    getMaterialVariantDimensions(mid)
      .then((items) => {
        setVariantTemplate(items || []);
        if (byUser && items && items.length) setVariantModalOpen(true);
      })
      .catch((err) => {
        console.error("MATERIAL TEMPLATE LOAD ERROR", err);
      });
  }, [materialId]);

  const loadAudit = useCallback(async () => {
    if (!item?.id) return;
    setAuditLoading(true);
    try {
      const rows = await getSalesOrderItemAudit(Number(salesOrderId), Number(item.id));
      setAuditRows(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("SALES ORDER ITEM AUDIT LOAD ERROR", err);
    } finally {
      setAuditLoading(false);
    }
  }, [salesOrderId, item?.id]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  /** Düzenlemede yalnızca gerçekten değişen alanları döndürür. */
  function buildEditPayload() {
    const current = {
      material_id: materialId ? String(Number(materialId)) : "",
      material_variant_id: resolvedVariantId ?? null,
      customer_product_code: customerProductCode.trim(),
      inner_carton_code: innerCartonCode.trim(),
      outer_carton_code: outerCartonCode.trim(),
      product_name_text: productNameText.trim(),
      qty: qty === "" ? "" : String(Number(qty)),
      due_date: dueDate || "",
      po_date: poDate || "",
      pi_date: piDate || "",
      note: String(note || "").trim(),
      manufacturing_source: selectedMaterial?.has_manufacturing_source ? manufacturingSource : 1,
    };
    const initial = initialRef.current || {};
    const payload = {};
    for (const [key, value] of Object.entries(current)) {
      if (String(initial[key] ?? "") === String(value ?? "")) continue;
      if (key === "material_id" || key === "qty") payload[key] = Number(value);
      else if (key === "material_variant_id" || key === "manufacturing_source") payload[key] = value;
      else payload[key] = value === "" ? null : value;
    }
    return payload;
  }

  async function saveEdit() {
    // Düzenlemede zorunlu alan yoktur: dokunulmayan bilgi zaten kayıtta duruyor ve
    // gönderilmiyor. Bu yüzden doğrulama SADECE gönderilecek alanlara uygulanır.
    const payload = buildEditPayload();
    if (!Object.keys(payload).length) {
      onClose?.();
      return;
    }
    if ("material_id" in payload && !Number(materialId)) {
      setError("Malzeme seçin.");
      return;
    }
    if ("customer_product_code" in payload && !customerProductCode.trim()) {
      setError("Müşteri ürün kodu boş bırakılamaz.");
      return;
    }
    if ("qty" in payload && (!Number.isFinite(Number(qty)) || Number(qty) <= 0)) {
      setError("Miktar 0'dan büyük olmalı.");
      return;
    }
    // Bu malzeme varyant gerektiriyorsa (variantTemplate dolu), hangi alan
    // değişmiş olursa olsun varyantsız kayıt YAPILAMAZ. material_id'nin
    // payload'da olup olmamasına bakılmaz: malzeme kutusunda gezinip aynı
    // malzemeye geri dönmek gibi dolaylı yollarla da varyant boşa düşebilir
    // (ör. 30 → 34 → 30) — bu kontrol olmadan öyle bir durum sessizce
    // kaydedilip mevcut varyantı veritabanından siler.
    if (variantTemplate.length && !resolvedVariantId) {
      setError("Bu malzeme için varyant seçilmeli.");
      setVariantModalOpen(true);
      return;
    }

    setSaving(true);
    try {
      await updateSalesOrderItem(Number(salesOrderId), Number(item.id), payload);
      await onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("SALES ORDER ITEM SAVE ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setError(`Kalem güncellenemedi${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    setError("");
    if (isEdit) return saveEdit();

    const mid = materialId ? Number(materialId) : null;
    if (!mid) {
      setError("Malzeme seçin.");
      return;
    }
    if (!customerProductCode.trim()) {
      setError("M\u00fc\u015fteri \u00fcr\u00fcn kodu zorunludur.");
      return;
    }
    if (variantTemplate.length && !resolvedVariantId) {
      setError("Lütfen varyant seçin.");
      setVariantModalOpen(true);
      return;
    }
    if (!Number.isFinite(Number(qty)) || Number(qty) <= 0) {
      setError("Miktar > 0 olmalı.");
      return;
    }

    setSaving(true);
    try {
      const baseNote = String(note || "").trim();

      const isPrintedMaterial =
        (selectedMaterial?.code && String(selectedMaterial.code).includes(".80")) ||
        (selectedMaterial?.name && String(selectedMaterial.name).toLocaleLowerCase("tr-TR").includes("bask"));

      await createSalesOrderItem(Number(salesOrderId), {
        material_id: mid,
        material_variant_id: resolvedVariantId,
        customer_product_code: customerProductCode.trim(),
        inner_carton_code: innerCartonCode.trim() || null,
        outer_carton_code: outerCartonCode.trim() || null,
        product_name_text: productNameText.trim() || null,
        qty: Number(qty),
        due_date: dueDate || null,
        po_date: poDate || null,
        pi_date: piDate || null,
        is_printed: isPrintedMaterial ? 1 : 0,
        manufacturing_source: selectedMaterial?.has_manufacturing_source ? manufacturingSource : 1,
        note: baseNote || null,
      });
      await onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("SALES ORDER ITEM SAVE ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setError(`Kalem eklenemedi${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  const selectedMaterial = materialId
    ? materials.find((m) => Number(m.id) === Number(materialId))
    : null;

  // Rezervasyonu etkileyebilecek (yapısal) bir alan değişti mi?
  const initialSnapshot = initialRef.current;
  const structuralChanged =
    isEdit && initialSnapshot
      ? String(initialSnapshot.material_id) !== String(materialId) ||
        String(initialSnapshot.material_variant_id ?? "") !== String(resolvedVariantId ?? "") ||
        String(initialSnapshot.qty) !== String(Number(qty))
      : false;

  return (
    <>
      <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", fontSize: 20 }}>{isEdit ? "✎" : "+"}</Box>
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  {isEdit ? `Kalemi Düzenle${item?.line_no ? ` · ${item.line_no}. satır` : ""}` : "Satış Siparişi Kalemi Ekle"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {isEdit
                    ? "Sadece değiştirdiğiniz alanlar güncellenir, diğer bilgiler aynen korunur."
                    : "Ürün, varyant, miktar ve teslim bilgilerini girerek yeni kalem oluşturun."}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
          {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert> : null}
          {isEdit && Number(item?.reservation_count) > 0 && structuralChanged ? (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Bu kalemde kâğıt rezervasyonu var; malzeme, varyant veya miktar değişikliği rezervasyona otomatik yansımaz.
            </Alert>
          ) : null}
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
            <Box sx={{ mb: 2 }}>
              <Typography fontWeight={800}>Kalem bilgileri</Typography>
              <Typography variant="body2" color="text.secondary">Ürün, müşteri bilgileri, paket miktarı ve teslim tarihini bu alandan yönetin.</Typography>
            </Box>
            <Stack spacing={2} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" } }}>

            <FormControl size="small" disabled={saving}>
              <InputLabel id="material-label">Malzeme</InputLabel>
              <Select
                labelId="material-label"
                label="Malzeme"
                value={materialId}
                onChange={(e) => {
                  // Malzeme kutusunda gezinip aynı malzemeye geri dönülürse
                  // (ör. 30 → 34 → 30) varyant sıfırlanmamalı; aksi halde
                  // hiçbir şey değişmemiş görünse de varyant sessizce kaybolur.
                  if (String(e.target.value) !== String(materialId)) {
                    userPickedMaterialRef.current = true;
                  }
                  setMaterialId(e.target.value);
                }}
              >
                <MenuItem value="">
                  <em>Seçiniz</em>
                </MenuItem>
                {/* Liste henüz yüklenmediyse (veya kalemin malzemesi bu filtrede yoksa)
                    kutu boş görünmesin diye kalemin kendi malzemesi seçenek olarak eklenir. */}
                {isEdit && materialId && !materials.some((m) => String(m.id) === String(materialId)) ? (
                  <MenuItem value={String(materialId)}>
                    {item.material_code} - {item.material_name}
                  </MenuItem>
                ) : null}
                {materials.map((m) => (
                  <MenuItem key={m.id} value={String(m.id)}>
                    {m.code} - {m.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {materialId && variantTemplate.length ? (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {resolvedVariantId ? (
                    <>
                      Seçilen varyant: <b>{resolvedVariantDisplay}</b>
                    </>
                  ) : (
                    "Bu malzeme için varyant seçimi gerekli."
                  )}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => setVariantModalOpen(true)} disabled={saving}>
                  {resolvedVariantId ? "Değiştir" : "Varyant Seç"}
                </Button>
              </Stack>
            ) : null}

            {selectedMaterial?.has_manufacturing_source ? (
              <FormControl size="small" disabled={saving}>
                <InputLabel id="mfg-source-label">Üretim kaynağı</InputLabel>
                <Select
                  labelId="mfg-source-label"
                  label="Üretim kaynağı"
                  value={manufacturingSource}
                  onChange={(e) => setManufacturingSource(Number(e.target.value))}
                >
                  <MenuItem value={1}>Bobin</MenuItem>
                  <MenuItem value={2}>Fason</MenuItem>
                </Select>
              </FormControl>
            ) : null}

            <TextField
              label={"M\u00fc\u015fteri \u00dcr\u00fcn Kodu"}
              value={customerProductCode}
              onChange={(e) => setCustomerProductCode(e.target.value)}
              disabled={saving}
              required
            />
            <TextField
              label={"\u0130\u00e7 Koli Kodu (opsiyonel)"}
              value={innerCartonCode}
              onChange={(e) => setInnerCartonCode(e.target.value)}
              disabled={saving}
            />
            <TextField
              label={"D\u0131\u015f Koli Kodu (opsiyonel)"}
              value={outerCartonCode}
              onChange={(e) => setOuterCartonCode(e.target.value)}
              disabled={saving}
            />
            <TextField
              label="Miktar (Paket)"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              disabled={saving}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Termin"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="PO Tarihi (opsiyonel)"
                type="date"
                value={poDate}
                onChange={(e) => setPoDate(e.target.value)}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="PI Tarihi (opsiyonel)"
                type="date"
                value={piDate}
                onChange={(e) => setPiDate(e.target.value)}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <TextField
              label={"Ürün Adı (opsiyonel)"}
              value={productNameText}
              onChange={(e) => setProductNameText(e.target.value)}
              disabled={saving}
            />
            <TextField
              label="Not (opsiyonel)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={saving}
            />
            </Stack>
          </Paper>

          {isEdit ? (
            <Paper elevation={0} sx={{ mt: 2, p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
              <Typography fontWeight={800}>Değişiklik Geçmişi</Typography>
              <Typography variant="body2" color="text.secondary">
                Bu kalemde kimin ne zaman neyi değiştirdiği.
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              {auditLoading ? (
                <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={22} /></Stack>
              ) : auditRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Bu kalem henüz hiç değiştirilmedi.
                </Typography>
              ) : (
                <List dense disablePadding sx={{ maxHeight: 240, overflowY: "auto" }}>
                  {auditRows.map((row) => {
                    const fields = Object.keys(row.after_state || {});
                    return (
                      <ListItem key={row.id} disableGutters alignItems="flex-start" sx={{ borderBottom: "1px solid #f1f5f9", py: 1 }}>
                        <ListItemText
                          primary={
                            <Stack spacing={0.25}>
                              {fields.map((field) => (
                                <Typography key={field} variant="body2">
                                  <b>{FIELD_LABELS[field] || field}:</b>{" "}
                                  {formatAuditValue(field, row.before_state?.[field])}
                                  {" → "}
                                  <b>{formatAuditValue(field, row.after_state?.[field])}</b>
                                </Typography>
                              ))}
                            </Stack>
                          }
                          secondary={`${row.actor_name || "Bilinmiyor"} · ${formatAuditDateTime(row.created_at)}`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Paper>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e7eaf3", justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">* Malzeme, müşteri ürün kodu ve miktar zorunludur.</Typography>
          <Stack direction="row" spacing={1}><Button onClick={onClose} color="inherit" disabled={saving}>İptal</Button><Button onClick={save} variant="contained" disabled={saving} sx={{ px: 2.5, borderRadius: 2, boxShadow: "0 8px 18px rgba(79, 70, 229, 0.24)" }}>{saving ? "Kaydediliyor..." : isEdit ? "Değişiklikleri Kaydet" : "Kalemi Kaydet"}</Button></Stack>
        </DialogActions>
      </Dialog>

      {variantModalOpen && materialId ? (
        <MaterialVariantSelectModal
          materialId={Number(materialId)}
          materialName={selectedMaterial?.name || ""}
          templateDimensions={variantTemplate}
          optionalDimensionKeys={[
            "BOXWIDTH",
            "BOXLENGTH",
            "BOXHEIGHT",
            "KOLI_EN",
            "KOLI_BOY",
            "KOLI_YUKSEKLIK",
            "PANTONE",
            "PANTONE_CODE",
          ]}
          initialSelections={variantSelections}
          onClose={() => setVariantModalOpen(false)}
          onResolved={({ material_variant_id, display_name, selections }) => {
            setResolvedVariantId(Number(material_variant_id));
            setResolvedVariantDisplay(display_name || "");
            // Pencere tekrar açılırsa en son seçimlerle dolu gelsin.
            if (Array.isArray(selections)) setVariantSelections(selections);
          }}
        />
      ) : null}
    </>
  );
}
